import React from 'react';
import { Link } from 'react-router-dom';
import { PiggyBank, ShoppingCart, Utensils, Lightbulb, Bus, Target, LightbulbIcon, ArrowRight } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import Economia from '../components/calculators/Economia';

const Savings: React.FC = () => {
  useSEO({
    title: 'A Arte de Economizar: Dicas de Economia Doméstica e Regra 50/30/20 (2026) | O Teu Dinheiro',
    description: 'Domine a arte de economizar dinheiro ganhando pouco: Regra 50/30/20, calculadora de metas financeiras e estratégias práticas para reduzir gastos em 2026.',
    keywords: 'a arte de economizar, dicas de economia, como economizar dinheiro, regra 50 30 20, economia doméstica, planejamento financeiro familiar',
    url: 'https://www.oteudinheiro.com.br/economia'
  });
  const savingsTips = [
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      title: 'Lista de Compras',
      description: 'Nunca vá ao supermercado sem lista'
    },
    {
      icon: <Utensils className="w-6 h-6" />,
      title: 'Cozinhe em Casa',
      description: 'Evite delivery e restaurantes caros'
    },
    {
      icon: <Lightbulb className="w-6 h-6" />,
      title: 'Energia',
      description: 'Troque lâmpadas por LED'
    },
    {
      icon: <Bus className="w-6 h-6" />,
      title: 'Transporte',
      description: 'Use transporte público quando possível'
    }
  ];

  const budgetRule = [
    {
      percentage: '50%',
      title: 'Necessidades',
      description: 'Aluguel, alimentação, transporte, contas básicas',
      color: 'bg-blue-500'
    },
    {
      percentage: '30%',
      title: 'Desejos',
      description: 'Entretenimento, roupas, hobbies, viagens',
      color: 'bg-green-500'
    },
    {
      percentage: '20%',
      title: 'Economia',
      description: 'Poupança, investimentos, fundo de emergência',
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 to-teal-50 section-padding">
        <div className="container-custom text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Dicas de Economia: A Arte de Economizar
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Domine a arte de economizar no dia a dia com estratégias práticas para organizar 
            seu orçamento de forma inteligente em 2026.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Column - Budget Rule */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Regra dos 50/30/20
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Uma forma simples de organizar seu orçamento e garantir 
                  que você está economizando regularmente.
                </p>
                
                <div className="space-y-6">
                  {budgetRule.map((item, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className={`w-16 h-16 ${item.color} text-white rounded-full flex items-center justify-center font-bold text-lg`}>
                        {item.percentage}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {item.title}
                        </h3>
                        <p className="text-gray-600">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="tip-box">
                <div className="flex items-start space-x-3">
                  <LightbulbIcon className="w-6 h-6 text-secondary-600 mt-1" />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      💡 Dica Prática
                    </h4>
                    <p className="text-gray-700">
                      Comece economizando 10% da sua renda e aumente gradualmente. 
                      Pequenas mudanças fazem grande diferença no longo prazo. 
                      O importante é criar o hábito de economizar.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Savings Tips */}
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Dicas Práticas para o Dia a Dia
              </h2>
              
              <div className="grid gap-6">
                {savingsTips.map((tip, index) => (
                  <div key={index} className="card p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <div className="text-emerald-600">
                          {tip.icon}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {tip.title}
                        </h3>
                        <p className="text-gray-600">
                          {tip.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Tips Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Estratégias para Economizar Mais
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Além das dicas básicas, existem estratégias mais avançadas 
              que podem aumentar significativamente suas economias.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Metas Específicas
              </h3>
              <p className="text-gray-600 text-sm">
                Defina objetivos claros: "Vou economizar R$ 500 por mês 
                para uma viagem no final do ano."
              </p>
            </div>

            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <PiggyBank className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Automatize
              </h3>
              <p className="text-gray-600 text-sm">
                Configure transferências automáticas para sua conta de 
                poupança assim que receber o salário.
              </p>
            </div>

              <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Revise Gastos
              </h3>
              <p className="text-gray-600 text-sm">
                Analise seus gastos mensalmente e identifique 
                onde pode cortar despesas desnecessárias.
              </p>
            </div>
          </div>

          {/* Embedded Savings Calculator */}
          <div className="mt-16 bg-white p-8 rounded-2xl shadow-md border border-emerald-100">
            <Economia />
          </div>

          {/* Related Economy Guides */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Guias Práticos de Economia em Destaque
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/blog/carro-assinatura-calculadora"
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:border-emerald-400 hover:shadow-md transition-all"
              >
                <span className="text-xs font-semibold uppercase text-emerald-600">Calculadora</span>
                <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2">
                  Carro por Assinatura em 2026: Fizemos as Contas
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Compare IPVA, seguro, depreciação e CDI para descobrir se vale mais a pena assinar ou comprar.
                </p>
                <span className="inline-flex items-center text-sm font-semibold text-emerald-600">
                  Ver simulação <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <Link
                to="/blog/nomad-wise-global-2026"
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:border-emerald-400 hover:shadow-md transition-all"
              >
                <span className="text-xs font-semibold uppercase text-emerald-600">Câmbio e Viagem</span>
                <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2">
                  Nomad ou Wise? Comparativo de Conta Global 2026
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Veja como economizar no IOF e no spread cambial em viagens para os EUA e Europa.
                </p>
                <span className="inline-flex items-center text-sm font-semibold text-emerald-600">
                  Comparar contas <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <Link
                to="/blog/renda-extra-2026-tendencias"
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:border-emerald-400 hover:shadow-md transition-all"
              >
                <span className="text-xs font-semibold uppercase text-emerald-600">Renda Extra</span>
                <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2">
                  Como Fazer Renda Extra em 2026: 5 Formas Reais
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Ideias práticas sem investimento inicial para aumentar sua capacidade de poupança mensal.
                </p>
                <span className="inline-flex items-center text-sm font-semibold text-emerald-600">
                  Ler guia <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Savings;
